import type { StateStorage } from 'zustand/middleware';
import { createJSONStorage, persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import { activeSpaceCookieName } from '@/constants/variables';
import type { SpaceListItem } from '@/types/navigation';
import { getRootDomainCookieOptions } from '@/utils/firebase-helpers';

import type { SpaceStore, SpaceStoreInitProps } from './types';

// ------------- Constants -------------

const EMPTY_SPACES: SpaceListItem[] = [];
const COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // 30 days

// ------------- Helper Functions -------------

/**
 * Check if a space ID is valid (non-empty string)
 */
function isValidSpaceId(spaceId: string | undefined | null): spaceId is string {
  return spaceId !== undefined && spaceId !== null && spaceId !== '';
}

// ------------- Cookie Storage Adapter -------------

/**
 * Synchronous StateStorage adapter for Zustand's persist middleware.
 * Uses document.cookie directly because Zustand's StateStorage interface
 * requires synchronous getItem/setItem (cookies-next's setCookie is async).
 *
 * Bridges Zustand's { state, version } format with a raw cookie string,
 * keeping it compatible with the legacy CMS (poc-cms) `lastUsedSpace` cookie.
 *
 * Domain is resolved via getRootDomainCookieOptions (NEXT_PUBLIC_JWT_DOMAIN_ROOT)
 * — the same env var used by JWT cookies — so all cookies share the root domain.
 */
const spaceCookieStateStorage: StateStorage = {
  getItem: (_name: string): string | null => {
    if (typeof document === 'undefined') {
      return null;
    }

    const cookieMatch = document.cookie.match(
      new RegExp(`(?:^|; )${activeSpaceCookieName}=([^;]*)`),
    );
    const cookieValue = cookieMatch !== null ? cookieMatch[1] : undefined;

    if (cookieValue === undefined || cookieValue === '') {
      return null;
    }

    // Wrap raw cookie value in Zustand persist format
    return JSON.stringify({
      state: { currentSpaceId: decodeURIComponent(cookieValue) },
      version: 0,
    });
  },

  setItem: (_name: string, value: string): void => {
    if (typeof document === 'undefined') {
      return;
    }

    try {
      const parsed = JSON.parse(value) as { state?: { currentSpaceId?: string } };
      const spaceId = parsed?.state?.currentSpaceId;

      if (spaceId === undefined || spaceId === null || spaceId === '') {
        return;
      }

      const { domain } = getRootDomainCookieOptions();
      const domainPart = domain !== undefined ? `; domain=${domain}` : '';

      document.cookie = `${activeSpaceCookieName}=${encodeURIComponent(spaceId)}; path=/${domainPart}; max-age=${COOKIE_MAX_AGE_SECONDS}; SameSite=Lax`;
    } catch {
      // Ignore JSON parse errors
    }
  },

  removeItem: (_name: string): void => {
    if (typeof document === 'undefined') {
      return;
    }

    const { domain } = getRootDomainCookieOptions();
    const domainPart = domain !== undefined ? `; domain=${domain}` : '';

    document.cookie = `${activeSpaceCookieName}=; path=/${domainPart}; max-age=0`;
  },
};

// ------------- Store Factory -------------

/**
 * Creates a Zustand store for space management with cookie persistence.
 *
 * IMPORTANT: Never validate user's space selection against availableSpaces.
 * With pagination, we can never guarantee all spaces are loaded (e.g., user's
 * space might be #51 but only first 50 are fetched). Trust the user's selection.
 *
 * Features:
 * - Single source of truth: URL path determines the active space
 * - Cookie persistence: last used space is saved to a root-domain cookie
 *   so both the dashboard (Next.js) and legacy CMS share the same value
 * - skipHydration: true prevents cookie from overriding server-provided space
 *   on initial render (avoids hydration mismatches in Next.js SSR)
 * - Auto-selection ONLY when user has no space (empty string)
 *
 * Priority for initial space:
 * 1. configSpaceId from server (already resolved from URL path/claims/API)
 * 2. First available space from initialSpaces
 * 3. Empty string (no space)
 */
export function createSpaceStore(initProps: SpaceStoreInitProps = {}) {
  const { configSpaceId, initialSpaces = EMPTY_SPACES } = initProps;

  // Determine initial space ID
  let initialSpaceId = '';

  // Priority 1: Use config space if provided and valid
  if (isValidSpaceId(configSpaceId)) {
    initialSpaceId = configSpaceId;
  } else if (initialSpaces.length > 0) {
    // Priority 2: If no config space but we have initial spaces, select first
    const firstSpace = initialSpaces[0];
    if (firstSpace !== undefined) {
      initialSpaceId = firstSpace.id;
    }
  }

  return createStore<SpaceStore>()(
    persist(
      (set, get) => ({
        // ------------- Initial State -------------
        currentSpaceId: initialSpaceId,
        isHydrated: false,
        availableSpaces: initialSpaces,

        // ------------- Actions -------------

        setCurrentSpace: (spaceId: string): void => {
          const { currentSpaceId: previousSpaceId } = get();

          // Skip if space hasn't changed
          if (spaceId === previousSpaceId) {
            return;
          }

          // Update store state — persist middleware automatically writes cookie
          set({ currentSpaceId: spaceId });
        },

        setHydrated: (hydrated: boolean): void => {
          set({ isHydrated: hydrated });
        },

        setAvailableSpaces: (spaces: SpaceListItem[]): void => {
          const { currentSpaceId, autoSelectFirstSpace } = get();

          // Update available spaces
          set({ availableSpaces: spaces });

          // NEVER check if currentSpaceId exists in spaces list - pagination means
          // user's space may not be loaded. Only auto-select when NO space is set.
          if (isValidSpaceId(currentSpaceId) === false) {
            setTimeout(() => {
              autoSelectFirstSpace();
            }, 0);
          }
        },

        autoSelectFirstSpace: (): void => {
          const { currentSpaceId, availableSpaces, setCurrentSpace } = get();

          // Don't auto-select if no spaces available
          if (availableSpaces.length === 0) {
            return;
          }

          // NEVER validate currentSpaceId against availableSpaces - just check if empty
          if (isValidSpaceId(currentSpaceId) === false) {
            const firstSpace = availableSpaces[0];
            if (firstSpace !== undefined) {
              setCurrentSpace(firstSpace.id);
            }
          }
        },

        clearSpace: (): void => {
          set({
            currentSpaceId: '',
            availableSpaces: EMPTY_SPACES,
          });
        },
      }),
      {
        name: 'space-cookie-storage',
        storage: createJSONStorage(() => spaceCookieStateStorage),
        // Skip automatic hydration from cookie on store creation.
        // The URL path is the single source of truth — the server provides
        // configSpaceId from the URL, so we must NOT override it with the cookie.
        // The cookie is only read server-side as a fallback when visiting /dashboard
        // without a spaceId in the URL (see resolveSpaceForDashboardServer).
        skipHydration: true,
      },
    ),
  );
}

// ------------- Store Type -------------

export type SpaceStoreApi = ReturnType<typeof createSpaceStore>;
